import {useNavigation, useRoute} from '@react-navigation/native';
import React, {useRef, useState} from 'react';
import {View, StyleSheet, ImageBackground, Text, TouchableOpacity, Alert} from 'react-native';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {useTranslation} from 'react-i18next';
import {GoBackHeader} from '~components/GoBackHeader';
import ModernButton from '~components/ModernButton';
import ModernCard from '~components/ModernCard/ModernCard';
import {NavigationProps} from '~types/navigation/navigation.type';
import {spacing, borderRadius, typography, shadows} from '~constants/design';
import StarRating from 'react-native-star-rating-widget';
import RatingSheet from '~components/RatingSheet';
import BottomSheet from '@gorhom/bottom-sheet';
import {ThemedStatusBar} from '~components/ThemedStatusBar/ThemedStatusBar';
import {useTheme} from '~contexts/ThemeContext';

import {useGetEventById} from '~hooks/event/useGetEventById';
import {useAddReviewByEventId} from '~hooks/event/useAddReviewByEventId';
import {useFlexibleReview} from '~hooks/event/useFlexibleReview';
import FlexibleReviewSheet from '~components/FlexibleReviewSheet/FlexibleReviewSheet';
import {useUserStore} from '~providers/userStore/zustand';
import QuizSuccessModal from '~components/QuizSuccessModal';

const successLottie = require('~assets/lottie/success.json');

const RateEvent: React.FC = () => {
  const {colors} = useTheme();
  const {t} = useTranslation();
  const bottomSheetRef = useRef<BottomSheet>(null);
  const navigation = useNavigation<NavigationProps>();
  const route = useRoute<any>();
  const insects = useSafeAreaInsets();

  const {data: event, isLoading} = useGetEventById(route.params.event.event_id);
  const {giveReview, isLoading: reviewLoading} = useAddReviewByEventId();
  const {submitFlexibleReview, isLoading: flexibleReviewLoading} = useFlexibleReview();
  const {user} = useUserStore();
  const [rating, setRating] = useState(0);
  const [isSubmitted, setSubmitted] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // Check if event requires flexible review
  const requiresFlexibleReview = event?.event_service_type && 
    ['CULTURAL_EDIT', 'HOUSEWARMING', 'THE_REST_TABLE'].includes(event.event_service_type);

  const goBackHandler = () => {
    navigation.goBack();
  };

  const onSubmit1 = () => {
    setSubmitted(true);
    bottomSheetRef.current?.snapToIndex(0);
  };

  const onSubmit = async (data: {comment: string; rating: number; companyRating: number}) => {
    bottomSheetRef.current?.close();
    giveReview(
      {event_id: event?.event_id || 0, comment: data.comment, rating: data.rating},
      {
        onSuccess: data => {
          setShowSuccess(true);
        },
        onError: error => {
          console.log(error, 'error');
        },
      },
    );
  };

  const onFlexibleSubmit = async (metadata: Record<string, any>) => {
    bottomSheetRef.current?.close();
    submitFlexibleReview(
      {
        event_id: event?.event_id || 0,
        metadata: metadata,
        user_id: user?.email || user?.uid || '',
        event_type: event?.event_service_type === 'THE_REST_TABLE' ? 'RESET_TABLE' : (event?.event_service_type || '')
      },
      {
        onSuccess: data => {
          setShowSuccess(true);
        },
        onError: error => {
          console.log(error, 'flexible review error');
          Alert.alert('Error', 'Failed to submit review. Please try again.');
        },
      },
    );
  };

  const styles = StyleSheet.create({
    eventNameText: {
      textAlign: 'center',
      fontWeight: '700',
      color: colors.white,
      fontSize: 24,
      marginBottom: 12,
      position: 'absolute',
      zIndex: 10000,
      textShadowColor: 'rgba(0, 0, 0, 0.8)',
      textShadowOffset: {width: 0, height: 2},
      textShadowRadius: 4,
      letterSpacing: 0.5,
    },
    buttonText: {
      color: colors.white,
      fontWeight: '600',
      textAlign: 'center',
    },
    button: {
      backgroundColor: colors.secondary,
      paddingVertical: 10,
      width: '88%',
      borderRadius: 20,
      marginHorizontal: 10,
      marginTop: 30,
    },
    eventTitle: {
      textAlign: 'center',
      fontWeight: 'bold',
      color: colors.white,
      fontSize: 20,
      marginBottom: 8,
    },
    container: {flex: 1, backgroundColor: colors.white},
    overlay: {
      ...StyleSheet.absoluteFillObject,
      backgroundColor: 'rgba(0, 0, 0, 0.4)',
    },
  });

  return (
    <View style={styles.container}>
      <ThemedStatusBar />
      <GoBackHeader customCallback={goBackHandler} isGradientShow={false} />
      <ImageBackground
        style={[
          styles.container,
          {justifyContent: 'flex-end', alignItems: 'center', paddingBottom: insects.bottom + spacing.xl},
        ]}
        source={{uri: event?.image_url}}>
        {/* Event Name (shown after submission) */}
        {isSubmitted && (
          <Text
            style={[styles.eventNameText, {top: insects.top + 45}]}>
            {event?.name}
          </Text>
        )}

        {/* Overlay */}
        <View style={styles.overlay} />

        {/* Rating Content */}
        <View
          style={{alignItems: 'center', paddingHorizontal: spacing.lg, width: '100%'}}>
          <ModernCard
            variant="elevated"
            padding="xl"
            style={{
              backgroundColor: colors.cardBackground + 'F0', // More opaque for better readability
              marginBottom: spacing.xl,
              borderRadius: 24,
              shadowColor: colors.black,
              shadowOffset: {width: 0, height: 8},
              shadowOpacity: 0.15,
              shadowRadius: 16,
              elevation: 12,
              width: '100%',
            }}>
            {requiresFlexibleReview ? (
              <>
                <Text
                  style={{
                    fontSize: typography.fontSize['2xl'],
                    fontWeight: typography.fontWeight.bold,
                    color: colors.textPrimary,
                    textAlign: 'center',
                    marginBottom: spacing.sm,
                    lineHeight: 32,
                  }}>
                  Share Your Experience
                </Text>
                <Text
                  style={{
                    fontSize: typography.fontSize.base,
                    color: colors.textSecondary,
                    textAlign: 'center',
                    marginBottom: spacing.xl,
                    lineHeight: 22,
                  }}>
                  Help us improve future events with your feedback
                </Text>
                <ModernButton
                  onPress={onSubmit1}
                  title="Start Review"
                  variant="primary"
                  size="lg"
                  fullWidth
                  loading={flexibleReviewLoading}
                  hapticFeedback
                  style={{
                    backgroundColor: colors.primary,
                    shadowColor: colors.primary,
                    shadowOffset: {width: 0, height: 4},
                    shadowOpacity: 0.3,
                    shadowRadius: 8,
                    elevation: 8,
                  }}
                />
              </>
            ) : (
              <>
                <Text
                  style={{
                    fontSize: typography.fontSize['2xl'],
                    fontWeight: typography.fontWeight.bold,
                    color: colors.textPrimary,
                    textAlign: 'center',
                    marginBottom: spacing.lg,
                    textShadowColor: 'rgba(0, 0, 0, 0.3)',
                    textShadowOffset: {width: 0, height: 1},
                    textShadowRadius: 2,
                  }}>
                  {t('events.rate_experience') || `What did you think of\n${event?.name}?`}
                </Text>

                <View>
                  <StarRating
                    rating={rating}
                    onChange={setRating}
                    color={colors.warning}
                    emptyColor={colors.gray400}
                    starSize={40}
                    style={{marginBottom: spacing.lg}}
                  />
                </View>

                <ModernButton
                  onPress={onSubmit1}
                  title={t('generic.submit') || 'Submit Rating'}
                  variant="primary"
                  size="lg"
                  fullWidth
                  disabled={rating === 0}
                  loading={reviewLoading}
                  hapticFeedback
                  style={{
                    backgroundColor: colors.primary,
                    shadowColor: colors.primary,
                    shadowOffset: {width: 0, height: 4},
                    shadowOpacity: 0.3,
                    shadowRadius: 8,
                    elevation: 8,
                  }}
                />
              </>
            )}
          </ModernCard>
        </View>
      </ImageBackground>

      {requiresFlexibleReview ? (
        <FlexibleReviewSheet 
          bottomSheetRef={bottomSheetRef} 
          eventType={event?.event_service_type || ''} 
          onSubmit={onFlexibleSubmit}
          event_id={route.params.event.event_id}
        />
      ) : (
        <RatingSheet bottomSheetRef={bottomSheetRef} rate={rating} onSubmit={onSubmit} />
      )}

      <QuizSuccessModal 
        visible={showSuccess} 
        lottieSource={successLottie}
        title="Thank you!"
        description="Your review has been submitted successfully. Redirecting..."
        onClose={() => {
          setShowSuccess(false);
          navigation.goBack();
        }}
      />
    </View>
  );
};

export default RateEvent;
